import React, { useEffect, useMemo, useRef, useState } from "react";
import { toBlob, toPng } from "html-to-image";
import "./Ticket.css";
import ticket from "../assets/images/ticket.png";
import logoLight from "../assets/images/logo-light.png";
import paidStamp from "../assets/images/paid.png";
import failed from "../assets/images/failed.png";
import pending from "../assets/images/pending.png";


import a from "../assets/images/l.jpg";
import b from "../assets/images/k.jpg";
import c from "../assets/images/j.jpg";
import d from "../assets/images/e.jpg";
import e from "../assets/images/b.jpg";
import f from "../assets/images/c.jpg";

const Ticket = ({ order, variant = "success" }) => {
  // Array of image paths from public folder
  const allImages = [a, b, c, d, e, f];

  const eventDetails = order?.eventDetails || order?.eventId || {};
  const eventDateValue = eventDetails.date;
  const eventTimeValue = eventDetails.time;
  const targetDate = useMemo(() => {
    if (!eventDateValue) return new Date("2026-02-14T17:00:00");
    const baseDate = new Date(eventDateValue);
    if (Number.isNaN(baseDate.getTime())) {
      return new Date("2026-02-14T17:00:00");
    }
    const match =
      typeof eventTimeValue === "string"
        ? eventTimeValue.match(/^(\d{1,2}):(\d{2})/)
        : null;
    if (match) {
      baseDate.setHours(Number(match[1]), Number(match[2]), 0, 0);
    }
    return baseDate;
  }, [eventDateValue, eventTimeValue]);
  const [timeLeft, setTimeLeft] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [downloadMessage, setDownloadMessage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [qrRenderUrl, setQrRenderUrl] = useState("");
  const ticketRef = useRef(null);
  const qrImageRef = useRef(null);

  useEffect(() => {
    const pad = (value) => String(value).padStart(2, "0");
    const updateTimer = () => {
      const now = new Date();
      const diff = targetDate.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft({ days: "00", hours: "00", minutes: "00", seconds: "00" });
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      setTimeLeft({
        days: pad(days),
        hours: pad(hours),
        minutes: pad(minutes),
        seconds: pad(seconds),
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const formatEventDate = (value) => {
    if (!value) return "Sat 14th February";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Sat 14th February";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "long",
    });
  };

  const formatEventTime = (value) => {
    if (!value) return "03:00 PM Onwards";
    if (/^\d{1,2}:\d{2}$/.test(value)) {
      const [hours, minutes] = value.split(":").map(Number);
      const temp = new Date();
      temp.setHours(hours, minutes, 0, 0);
      return `${temp.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} Onwards`;
    }
    return value;
  };

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
  const locationLabel = [eventDetails.venue, eventDetails.city, eventDetails.state, eventDetails.country]
      .filter(Boolean)
      .join(", ") ||
    "Mosari Cafe, CP 67, Airport Road, Chandigarh";
  const ticketTypeLabel =
    order?.gender === "female" ? "Female Ticket" : "Male Ticket";
  const ticketDesc =
    order?.gender === "female"
      ? "Standard ticket for 1 female"
      : "Standard ticket for 1 male";
  const amountValue = Number(order?.amount);
  const amountLabel = Number.isFinite(amountValue)
    ? `Rs.${amountValue}`
    : "Rs.11999";
  const statusValue = (order?.status || "").toLowerCase();
  let statusBadge = null;
  if (statusValue === "paid") {
    statusBadge = { src: paidStamp, alt: "Paid" };
  } else if (statusValue === "failed") {
    statusBadge = { src: failed, alt: "Failed" };
  } else if (statusValue === "pending") {
    statusBadge = { src: pending, alt: "Pending" };
  }
  const normalizeQrUrl = (value) => {
    if (!value) return "";
    if (typeof value === "object") {
      return normalizeQrUrl(
        value.url || value.path || value.qrCode || value.qrcode || "",
      );
    }
    const text = String(value).trim();
    if (!text) return "";
    if (/^(data:|blob:)/.test(text)) return text;
    // if (/^https?:\/\/localhost(:\d+)?/i.test(text) && apiBaseUrl) {
    //   return text.replace(/^https?:\/\/localhost(:\d+)?/i, apiBaseUrl);
    // }
    if (/^https?:\/\//i.test(text)) {
      if (apiBaseUrl) {
        try {
          const parsed = new URL(text);
          if (parsed.pathname.startsWith("/uploads/")) {
            const base = apiBaseUrl.replace(/\/$/, "");
            return `${base}${parsed.pathname}${parsed.search}`;
          }
        } catch {
          // Ignore invalid URLs and fall through.
        }
      }
      return text;
    }
    if (/^[A-Za-z0-9+/=]+$/.test(text) && text.length > 100) {
      return `data:image/png;base64,${text}`;
    }
    if (!apiBaseUrl) return text;
    const base = apiBaseUrl.replace(/\/$/, "");
    const pathValue = text.startsWith("/") ? text : `/${text}`;
    return `${base}${pathValue}`;
  };
  const rawQrValue =
    order?.qrcode ||
    order?.qrCode ||
    order?.qrCodeUrl ||
    order?.qr_code ||
    order?.orderDetails?.qrcode ||
    order?.orderDetails?.qrCode ||
    order?.orderDetails?.qrCodeUrl ||
    order?.eventDetails?.qrcode ||
    order?.eventDetails?.qrCode ||
    order?.eventDetails?.qrCodeUrl;
  const qrCodeUrl =
    normalizeQrUrl(rawQrValue);
    
  const fallbackQrSvg = "data:image/gif;base64,R0lGODlhAQABAAAAACw=";
  const qrDisplayUrl = qrRenderUrl || qrCodeUrl || fallbackQrSvg;

  useEffect(() => {
    let cancelled = false;
    const setUrl = (value) => {
      if (!cancelled) setQrRenderUrl(value);
    };
    if (!qrCodeUrl) {
      setUrl("");
      return () => {
        cancelled = true;
      };
    }
    if (/^(data:|blob:)/.test(qrCodeUrl)) {
      setUrl(qrCodeUrl);
      return () => {
        cancelled = true;
      };
    }
    const loadQr = async () => {
      try {
        const token = localStorage.getItem("token");
        const shouldAttachToken = token && /\/uploads\//.test(qrCodeUrl);
        const response = await fetch(qrCodeUrl, {
          headers: shouldAttachToken
            ? { authorization: `Bearer ${token}` }
            : undefined,
        });
        if (!response.ok) throw new Error("QR fetch failed");
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setUrl(reader.result || fallbackQrSvg);
        reader.readAsDataURL(blob);
      } catch (error) {
        setUrl(fallbackQrSvg);
      }
    };
    loadQr();
    return () => {
      cancelled = true;
    };
  }, [qrCodeUrl, fallbackQrSvg]);

  const triggerDownload = (href, filename) => {
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    link.click();
  };

  const waitForImages = async (node) => {
    if (!node) return;
    const images = Array.from(node.querySelectorAll("img"));
    await Promise.all(
      images.map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          const cleanup = () => {
            img.onload = null;
            img.onerror = null;
            resolve();
          };
          img.onload = cleanup;
          img.onerror = cleanup;
        });
      }),
    );
  };

  const waitForImageLoad = (img) =>
    new Promise((resolve) => {
      if (!img) {
        resolve();
        return;
      }
      if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
      }
      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
        resolve();
      };
      img.onload = cleanup;
      img.onerror = cleanup;
    });

  const handleDownloadClick = async () => {
    if (!ticketRef.current || isDownloading) return;
    setIsDownloading(true);
    setDownloadMessage("");
    const qrNode = qrImageRef.current;
    const originalQrSrc = qrNode ? qrNode.src : "";
    if (qrNode) {
      qrNode.src = qrRenderUrl || fallbackQrSvg;
      await waitForImageLoad(qrNode);
    }
    try {
      await waitForImages(ticketRef.current);
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const blob = await toBlob(ticketRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        triggerDownload(blobUrl, "event-ticket.png");
        URL.revokeObjectURL(blobUrl);
      } else {
        const dataUrl = await toPng(ticketRef.current, {
          cacheBust: true,
          pixelRatio: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        triggerDownload(dataUrl, "event-ticket.png");
      }
      setDownloadMessage("Ticket download successful.");
      window.setTimeout(() => setDownloadMessage(""), 2500);
    } catch (error) {
      setDownloadMessage("Ticket download failed. Please try again.");
      console.error("Download error:", error);
      window.setTimeout(() => setDownloadMessage(""), 3000);
    } finally {
      if (qrNode && originalQrSrc) {
        qrNode.src = originalQrSrc;
      }
      setIsDownloading(false);
    }
  };

  const isSuccessVariant = variant === "success";

  return (
    <section className="sup-experience-section" id="about">
      {/* Left side images - Slider part */}
      <div className="sup-side-images sup-left-images">
        <div className="sup-image-scroll-group">
          <div className="sup-image-box">
            <img src={allImages[0]} alt="Event" />
          </div>
          <div className="sup-image-box">
            <img src={allImages[1]} alt="Event" />
          </div>
          <div className="sup-image-box">
            <img src={allImages[2]} alt="Event" />
          </div>
          <div className="sup-image-box">
            <img src={allImages[3]} alt="Event" />
          </div>
          {/* Duplicate for seamless loop slider */}
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[0]} alt="Event" />
          </div>
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[1]} alt="Event" />
          </div>
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[2]} alt="Event" />
          </div>
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[3]} alt="Event" />
          </div>
        </div>
      </div>

      {/* Main content - Ticket Design Added Here */}
      <div className="sup-experi-content">
        <div className="sup-experience-content">
          {isSuccessVariant && (
            <>
              <div className="sup-check-wrapper">✓</div>
              <h1 className="sup-main-thankyou">Thank you</h1>
              <p className="sup-success-msg">
                Your ticket has been booked successfully
              </p>
            </>
          )}

          {!isSuccessVariant && (
            <div className="sup-timercard">
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.days}</div>
                <div className="sup-timer-label">Days</div>
              </div>
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.hours}</div>
                <div className="sup-timer-label">hours</div>
              </div>
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.minutes}</div>
                <div className="sup-timer-label">minutes</div>
              </div>
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.seconds}</div>
                <div className="sup-timer-label">seconds</div>
              </div>
            </div>
          )}

          <div className="dwnimg" ref={ticketRef}>
            <div className="sup-ticketcard">
              {/* Top Tag */}
              <div className="sup-ticket-top-tag">
                <img
                  src={ticket}
                  alt="Ticket tag"
                  className="sup-ticket-tag-image"
                />
              </div>

              <div className="sup-ticket-inner">
                {/* QR Code Section */}
                <div className="sup-qr-container">
                  <div className="sup-qr-box">
                    <img
                      ref={qrImageRef}
                      src={qrDisplayUrl || fallbackQrSvg}
                      alt="QR"
                      crossOrigin="anonymous"
                      onError={(event) => {
                        event.currentTarget.src = fallbackQrSvg;
                      }}
                    />
                    <div className="sup-scan-text">SCAN ME</div>
                  </div>
                </div>

                {/* Details Section */}
                <div className="sup-details-container">
                  {statusBadge && (
                    <img src={statusBadge.src} alt={statusBadge.alt} className="sup-paid-stamp" />
                  )}
                  <h2 className="sup-ticket-date">
                    {formatEventDate(eventDateValue)}
                  </h2>
                  <p className="sup-ticket-loc">{locationLabel}</p>
                  <p className="sup-ticket-time">
                    {formatEventTime(eventTimeValue)}
                  </p>

                  <div className="sup-price-row" data-amount={amountLabel}>
                    <div className="sup-price-text">
                      <span className="sup-price-type">{ticketTypeLabel}</span>
                      <span className="sup-price-desc">{ticketDesc}</span>
                    </div>
                    <div className="sup-price-val">₹ 1999</div>
                  </div>
                </div>
              </div>
              <div className="sup-event-house-logo">
                <img
                  src={logoLight}
                  alt="Event House"
                  className="sup-event-house-image"
                />
              </div>
            </div>
          </div>

          <p className="sup-instruction-text">
            Download this ticket and show this at the time of entry
          </p>
          <button
            className="sup-download-btn"
            type="button"
            onClick={handleDownloadClick}
            disabled={isDownloading}
          >
            {isDownloading ? "Downloading..." : "Download Ticket"}
          </button>
          {downloadMessage ? (
            <div
              className="sup-download-message"
              role="status"
              aria-live="polite"
            >
              {downloadMessage}
            </div>
          ) : null}
          {isSuccessVariant && (
            <div className="sup-divider" aria-hidden="true" />
          )}
          {isSuccessVariant && (
            <div className="sup-event-details-title">Event Details</div>
          )}
          {isSuccessVariant && (
            <div className="sup-timercard">
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.days}</div>
                <div className="sup-timer-label">Days</div>
              </div>
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.hours}</div>
                <div className="sup-timer-label">hours</div>
              </div>
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.minutes}</div>
                <div className="sup-timer-label">minutes</div>
              </div>
              <div className="sup-timer-item">
                <div className="sup-timer-value">{timeLeft.seconds}</div>
                <div className="sup-timer-label">seconds</div>
              </div>
            </div>
          )}
          <div className="sup-event-details">
            <ol className="sup-event-details-list">
              <li>
                Do not forget to carry your Government ID for verification or
                get verified now by signing up.
              </li>
              <li>
                Reach at least 15-20 minutes early so that you get inside on
                time and do not miss any activity.
              </li>
              <li>
                Download this ticket or take a screenshot; it will also be sent
                to your email in a few minutes. You can always view your ticket
                in the My Events section.
              </li>
              <li>
                Entry will be permitted only upon successful ticket and ID
                verification.
              </li>
              <li>
                This ticket is valid only for the specified event, date, and
                time.
              </li>
              <li>
                Tickets once booked are non-refundable and non-cancellable,
                except in cases outlined in our Refund Policy.
              </li>
              <li>
                Entry may be denied if:
                <ul>
                  <li>Valid ID proof is not presented.</li>
                  <li>Ticket verification fails.</li>
                  <li>Venue capacity or safety guidelines require it.</li>
                </ul>
              </li>
              <li>
                Attendees are expected to behave respectfully at all times. Any
                form of harassment, inappropriate behavior, or disruption may
                result in removal from the event without refund.
              </li>
              <li>
                Event House is not responsible for personal belongings lost or
                damaged during the event.
              </li>
              <li>
                Photos and videos may be taken during the event for promotional
                purposes. If you do not wish to be featured, please inform the
                event team on arrival.
              </li>
              <li>
                By attending this event, you agree to comply with all Event
                House policies and on-ground guidelines.
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Right side images - Slider part */}
      <div className="sup-side-images sup-right-images">
        <div className="sup-image-scroll-group">
          <div className="sup-image-box">
            <img src={allImages[4]} alt="Event" />
          </div>
          <div className="sup-image-box">
            <img src={allImages[5]} alt="Event" />
          </div>
          <div className="sup-image-box">
            <img src={allImages[0]} alt="Event" />
          </div>
          <div className="sup-image-box">
            <img src={allImages[1]} alt="Event" />
          </div>
          {/* Duplicate for seamless loop slider */}
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[4]} alt="Event" />
          </div>
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[5]} alt="Event" />
          </div>
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[0]} alt="Event" />
          </div>
          <div className="sup-image-box" aria-hidden="true">
            <img src={allImages[1]} alt="Event" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Ticket;
